import {
  getReceivers,
  createReceiver,
  updateReceiver,
  deleteReceiver,
  createReceiverFormat,
  updateReceiverFormat,
  deleteReceiverFormat,
} from 'common/src/api/receivers';
import type { Receiver, ReceiverFormat } from 'common/src/api/receivers';
import { useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Card,
  CardBody,
  Skeleton,
  Chip,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import Header from '../../../components/layouts/Header';
import { ConfirmModal, PageLayout } from '../../../components/ui';
import { Icon } from '@iconify/react';

const SuperadminReceivers = () => {
  const [receivers, setReceivers] = useState<Receiver[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceiver, setSelectedReceiver] = useState<Receiver | null>(null);

  // Receiver form state
  const [receiverForm, setReceiverForm] = useState({ name: '', country: '', alternative_names: [] as string[] });
  const [altNameInput, setAltNameInput] = useState('');

  // Format form state
  const [selectedFormat, setSelectedFormat] = useState<ReceiverFormat | null>(null);
  const [formatForm, setFormatForm] = useState({ name: '', image_url: '' });

  // Modals
  const { isOpen: isReceiverModalOpen, onOpen: onReceiverModalOpen, onClose: onReceiverModalClose } = useDisclosure();
  const { isOpen: isDeleteReceiverOpen, onOpen: onDeleteReceiverOpen, onClose: onDeleteReceiverClose } = useDisclosure();
  const { isOpen: isFormatModalOpen, onOpen: onFormatModalOpen, onClose: onFormatModalClose } = useDisclosure();
  const { isOpen: isDeleteFormatOpen, onOpen: onDeleteFormatOpen, onClose: onDeleteFormatClose } = useDisclosure();
  const { isOpen: isFormatsListOpen, onOpen: onFormatsListOpen, onClose: onFormatsListClose } = useDisclosure();

  const [isEditing, setIsEditing] = useState(false);
  const [isEditingFormat, setIsEditingFormat] = useState(false);

  const fetchReceivers = async () => {
    setLoading(true);
    try {
      const data = await getReceivers();
      setReceivers(data);
    } catch (error) {
      console.error('Error fetching receivers:', error);
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReceivers();
  }, []);

  // ── Receiver handlers ──

  const handleNewReceiver = () => {
    setIsEditing(false);
    setSelectedReceiver(null);
    setReceiverForm({ name: '', country: '', alternative_names: [] });
    setAltNameInput('');
    onReceiverModalOpen();
  };

  const handleEditReceiver = (receiver: Receiver) => {
    setIsEditing(true);
    setSelectedReceiver(receiver);
    setReceiverForm({
      name: receiver.name,
      country: receiver.country || '',
      alternative_names: receiver.alternative_names || [],
    });
    setAltNameInput('');
    onReceiverModalOpen();
  };

  const handleDeleteReceiver = (receiver: Receiver) => {
    setSelectedReceiver(receiver);
    onDeleteReceiverOpen();
  };

  const handleReceiverSubmit = async () => {
    if (!receiverForm.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    const payload = {
      name: receiverForm.name.toUpperCase().trim(),
      country: receiverForm.country.toUpperCase().trim(),
      alternative_names: receiverForm.alternative_names,
    };

    try {
      if (isEditing && selectedReceiver) {
        await updateReceiver(selectedReceiver.id, payload);
        toast.success('Plantilla actualizada');
      } else {
        await createReceiver(payload);
        toast.success('Plantilla creada');
      }
      onReceiverModalClose();
      fetchReceivers();
    } catch (error) {
      console.error('Error saving receiver:', error);
      toast.error('Error al guardar plantilla');
    }
  };

  const handleDeleteReceiverConfirm = async () => {
    if (!selectedReceiver) return;
    try {
      await deleteReceiver(selectedReceiver.id);
      toast.success('Plantilla eliminada');
      onDeleteReceiverClose();
      fetchReceivers();
    } catch (error) {
      console.error('Error deleting receiver:', error);
      toast.error('Error al eliminar plantilla');
    }
  };

  const addAlternativeName = () => {
    const val = altNameInput.trim().toUpperCase();
    if (!val) return;
    if (receiverForm.alternative_names.includes(val)) {
      toast.warning('Nombre alternativo ya existe');
      return;
    }
    setReceiverForm({ ...receiverForm, alternative_names: [...receiverForm.alternative_names, val] });
    setAltNameInput('');
  };

  const removeAlternativeName = (name: string) => {
    setReceiverForm({
      ...receiverForm,
      alternative_names: receiverForm.alternative_names.filter((n) => n !== name),
    });
  };

  // ── Format handlers ──

  const handleOpenFormats = (receiver: Receiver) => {
    setSelectedReceiver(receiver);
    onFormatsListOpen();
  };

  const handleNewFormat = (receiver: Receiver) => {
    setIsEditingFormat(false);
    setSelectedReceiver(receiver);
    setSelectedFormat(null);
    setFormatForm({ name: '', image_url: '' });
    onFormatModalOpen();
  };

  const handleEditFormat = (receiver: Receiver, format: ReceiverFormat) => {
    setIsEditingFormat(true);
    setSelectedReceiver(receiver);
    setSelectedFormat(format);
    setFormatForm({ name: format.name, image_url: format.image_url || '' });
    onFormatModalOpen();
  };

  const handleDeleteFormat = (receiver: Receiver, format: ReceiverFormat) => {
    setSelectedReceiver(receiver);
    setSelectedFormat(format);
    onDeleteFormatOpen();
  };

  const handleFormatSubmit = async () => {
    if (!formatForm.name.trim()) {
      toast.error('El nombre del formato es requerido');
      return;
    }
    if (!selectedReceiver) return;

    try {
      if (isEditingFormat && selectedFormat) {
        await updateReceiverFormat(selectedFormat.id, {
          name: formatForm.name.trim(),
          image_url: formatForm.image_url.trim() || null,
        });
        toast.success('Formato actualizado');
      } else {
        await createReceiverFormat({
          name: formatForm.name.trim(),
          receiver_id: selectedReceiver.id,
          image_url: formatForm.image_url.trim() || null,
        });
        toast.success('Formato creado');
      }
      onFormatModalClose();
      fetchReceivers();
    } catch (error) {
      console.error('Error saving format:', error);
      toast.error('Error al guardar formato');
    }
  };

  const handleDeleteFormatConfirm = async () => {
    if (!selectedFormat) return;
    try {
      await deleteReceiverFormat(selectedFormat.id);
      toast.success('Formato eliminado');
      onDeleteFormatClose();
      fetchReceivers();
    } catch (error) {
      console.error('Error deleting format:', error);
      toast.error('Error al eliminar formato');
    }
  };

  // ── Render ──

  const columns = [
    { name: 'Nombre', uid: 'name' },
    { name: 'País', uid: 'country' },
    { name: 'Nombres Alternativos', uid: 'alternative_names' },
    { name: 'Formatos', uid: 'formats_count' },
    { name: 'Acciones', uid: 'actions' },
  ];

  const renderCell = (receiver: Receiver, columnKey: React.Key) => {
    switch (columnKey) {
      case 'name':
        return receiver.name;
      case 'country':
        return receiver.country || '-';
      case 'alternative_names':
        return (
          <div className='flex flex-wrap gap-1'>
            {(receiver.alternative_names || []).length > 0
              ? receiver.alternative_names.map((n) => (
                  <Chip key={n} size='sm' variant='flat'>
                    {n}
                  </Chip>
                ))
              : '-'}
          </div>
        );
      case 'formats_count':
        return (
          <Chip
            size='sm'
            variant='flat'
            color={receiver.receiver_formats?.length ? 'primary' : 'default'}
            className='cursor-pointer'
            onClick={() => handleOpenFormats(receiver)}
          >
            {receiver.receiver_formats?.length || 0}
          </Chip>
        );
      case 'actions':
        return (
          <div className='flex items-center gap-1'>
            <Button isIconOnly size='sm' variant='light' onClick={() => handleEditReceiver(receiver)}>
              <Icon icon='tabler:edit' className='text-default-400' />
            </Button>
            <Button isIconOnly size='sm' variant='light' color='danger' onClick={() => handleDeleteReceiver(receiver)}>
              <Icon icon='tabler:trash' className='text-danger-400' />
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  const formatsListReceiver = receivers.find((r) => r.id === selectedReceiver?.id);

  return (
    <PageLayout>
      <Header
        title='Plantillas'
        actions={
          <Button color='primary' onClick={handleNewReceiver}>
            Agregar Plantilla
          </Button>
        }
      />

      {loading ? (
        <Card className='w-full'>
          <CardBody>
            <div className='space-y-4'>
              <div className='grid grid-cols-5 gap-4'>
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className='h-8 w-full rounded-lg' />
                ))}
              </div>
              {[...Array(5)].map((_, rowIndex) => (
                <div key={rowIndex} className='grid grid-cols-5 gap-4'>
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className='h-12 w-full rounded-lg' />
                  ))}
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      ) : (
        <Table removeWrapper aria-label='Receivers table'>
          <TableHeader columns={columns}>
            {(column) => <TableColumn key={column.uid}>{column.name}</TableColumn>}
          </TableHeader>
          <TableBody items={receivers}>
            {(receiver) => (
              <TableRow key={receiver.id}>
                {(columnKey) => (
                  <TableCell>{renderCell(receiver, columnKey)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* Create/Edit Receiver Modal */}
      <Modal isOpen={isReceiverModalOpen} onClose={onReceiverModalClose}>
        <ModalContent>
          <ModalHeader>{isEditing ? 'Editar Plantilla' : 'Nueva Plantilla'}</ModalHeader>
          <ModalBody>
            <Input
              label='Nombre'
              value={receiverForm.name}
              onChange={(e) => setReceiverForm({ ...receiverForm, name: e.target.value })}
            />
            <Input
              label='País'
              value={receiverForm.country}
              onChange={(e) => setReceiverForm({ ...receiverForm, country: e.target.value })}
            />
            <div>
              <label className='block text-sm font-medium text-default-600 mb-1'>
                Nombres Alternativos
              </label>
              <div className='flex gap-2 mb-2'>
                <Input
                  size='sm'
                  placeholder='Agregar nombre alternativo'
                  value={altNameInput}
                  onChange={(e) => setAltNameInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addAlternativeName();
                    }
                  }}
                />
                <Button size='sm' color='primary' onClick={addAlternativeName}>
                  Agregar
                </Button>
              </div>
              <div className='flex flex-wrap gap-1'>
                {receiverForm.alternative_names.map((name) => (
                  <Chip key={name} size='sm' variant='flat' onClose={() => removeAlternativeName(name)}>
                    {name}
                  </Chip>
                ))}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onClick={onReceiverModalClose}>
              Cancelar
            </Button>
            <Button color='primary' onClick={handleReceiverSubmit}>
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Receiver Modal */}
      <ConfirmModal
        isOpen={isDeleteReceiverOpen}
        onClose={onDeleteReceiverClose}
        onConfirm={handleDeleteReceiverConfirm}
        title='Confirmar Eliminación'
        confirmLabel='Eliminar'
        cancelLabel='Cancelar'
        isDestructive
      >
        <div>
          ¿Estás seguro de que quieres eliminar <strong>{selectedReceiver?.name}</strong>?
          {(selectedReceiver?.receiver_formats?.length ?? 0) > 0 && (
            <p className='text-sm text-danger mt-2'>
              Se eliminarán también {selectedReceiver?.receiver_formats?.length} formato(s) asociado(s).
            </p>
          )}
        </div>
      </ConfirmModal>

      {/* Create/Edit Format Modal */}
      <Modal isOpen={isFormatModalOpen} onClose={onFormatModalClose}>
        <ModalContent>
          <ModalHeader>{isEditingFormat ? 'Editar Formato' : 'Nuevo Formato'}</ModalHeader>
          <ModalBody>
            <Input
              label='Nombre'
              value={formatForm.name}
              onChange={(e) => setFormatForm({ ...formatForm, name: e.target.value })}
            />
            <Input
              label='URL de Imagen (opcional)'
              value={formatForm.image_url}
              onChange={(e) => setFormatForm({ ...formatForm, image_url: e.target.value })}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onClick={onFormatModalClose}>
              Cancelar
            </Button>
            <Button color='primary' onClick={handleFormatSubmit}>
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Format Modal */}
      <ConfirmModal
        isOpen={isDeleteFormatOpen}
        onClose={onDeleteFormatClose}
        onConfirm={handleDeleteFormatConfirm}
        title='Confirmar Eliminación'
        confirmLabel='Eliminar'
        cancelLabel='Cancelar'
        isDestructive
      >
        <div>
          ¿Estás seguro de que quieres eliminar el formato <strong>{selectedFormat?.name}</strong>?
        </div>
      </ConfirmModal>

      {/* Formats List Modal */}
      <Modal isOpen={isFormatsListOpen} onClose={onFormatsListClose} size='2xl'>
        <ModalContent>
          <ModalHeader>Formatos de {formatsListReceiver?.name}</ModalHeader>
          <ModalBody>
            {(formatsListReceiver?.receiver_formats || []).length === 0 ? (
              <p className='text-sm text-default-400'>Sin formatos</p>
            ) : (
              <Table removeWrapper aria-label='Formats table'>
                <TableHeader>
                  <TableColumn>Nombre</TableColumn>
                  <TableColumn>Imagen</TableColumn>
                  <TableColumn>Acciones</TableColumn>
                </TableHeader>
                <TableBody>
                  {(formatsListReceiver?.receiver_formats || []).map((fmt) => (
                    <TableRow key={fmt.id}>
                      <TableCell>{fmt.name}</TableCell>
                      <TableCell>
                        {fmt.image_url ? (
                          <a href={fmt.image_url} target='_blank' rel='noopener noreferrer' className='text-primary underline text-sm'>
                            Ver imagen
                          </a>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-1'>
                          <Button isIconOnly size='sm' variant='light' onClick={() => handleEditFormat(formatsListReceiver!, fmt)}>
                            <Icon icon='tabler:edit' className='text-default-400' />
                          </Button>
                          <Button isIconOnly size='sm' variant='light' color='danger' onClick={() => handleDeleteFormat(formatsListReceiver!, fmt)}>
                            <Icon icon='tabler:trash' className='text-danger-400' />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onClick={onFormatsListClose}>
              Cerrar
            </Button>
            <Button size='sm' color='primary' startContent={<Icon icon='tabler:plus' />} onClick={() => { if (formatsListReceiver) handleNewFormat(formatsListReceiver); }}>
              Agregar Formato
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default SuperadminReceivers;
